import { useEffect, useRef, useState } from 'react';

const MARQUEE_THRESHOLD = 4;

const toBox = (a, b) => ({
  x: Math.min(a.x, b.x),
  y: Math.min(a.y, b.y),
  width: Math.abs(b.x - a.x),
  height: Math.abs(b.y - a.y),
});

const useMarqueeSelection = ({ stageRef, onSelect }) => {
  const [marquee, setMarquee] = useState(null);
  const onSelectRef = useRef(onSelect);
  const suppressClickRef = useRef(false);
  const cleanupRef = useRef(null);
  onSelectRef.current = onSelect;

  useEffect(() => () => cleanupRef.current?.(), []);

  const startMarquee = (options) => {
    const stage = stageRef.current;
    const origin = stage?.getPointerPosition();
    suppressClickRef.current = false;
    if (!origin) {
      return;
    }
    cleanupRef.current?.();
    let active = false;

    const getBox = (evt) => {
      stage.setPointersPositions(evt);
      const point = stage.getPointerPosition();
      if (!point) {
        return null;
      }
      if (!active && Math.hypot(point.x - origin.x, point.y - origin.y) < MARQUEE_THRESHOLD) {
        return null;
      }
      active = true;
      return toBox(origin, point);
    };

    const toDocument = (box) => {
      const transform = stage.getAbsoluteTransform().copy().invert();
      const end = { x: box.x + box.width, y: box.y + box.height };
      return toBox(transform.point({ x: box.x, y: box.y }), transform.point(end));
    };

    const handleMove = (evt) => {
      const box = getBox(evt);
      if (box) {
        setMarquee(toDocument(box));
      }
    };

    const handleUp = (evt) => {
      const box = getBox(evt);
      cleanup();
      setMarquee(null);
      if (active && box) {
        suppressClickRef.current = true;
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
        onSelectRef.current(box, options);
      }
    };

    const cleanup = () => {
      window.removeEventListener('mousemove', handleMove, true);
      window.removeEventListener('mouseup', handleUp, true);
      cleanupRef.current = null;
    };

    window.addEventListener('mousemove', handleMove, true);
    window.addEventListener('mouseup', handleUp, true);
    cleanupRef.current = cleanup;
  };

  const consumeClick = () => {
    const suppressed = suppressClickRef.current;
    suppressClickRef.current = false;
    return suppressed;
  };

  return { marquee, startMarquee, consumeClick };
};

export default useMarqueeSelection;
